import { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";

import { setUnauthorizedHandler } from "@/api/client";
import { Toaster } from "@/components/Toaster";
import { TotpPromptHost } from "@/components/TotpPrompt";
import { RouteChunkBoundary } from "@/pages/RouteChunkBoundary";
// Admin pages are loaded as a single dynamic chunk under
// `/assets/admin/*` (see vite.config.ts manualChunks). The FastAPI
// gate at the same path requires `admin` rights, so non-admins
// physically never download this code — `Giftery import`, provider
// names, admin labels, none of it ends up in the customer bundle.
const AdminAudit = lazy(() =>
  import("@/pages/AdminAudit").then((m) => ({ default: m.AdminAudit })),
);
const AdminCatalog = lazy(() =>
  import("@/pages/AdminCatalog").then((m) => ({ default: m.AdminCatalog })),
);
const AdminCodes = lazy(() =>
  import("@/pages/AdminCodes").then((m) => ({ default: m.AdminCodes })),
);
const AdminDashboard = lazy(() =>
  import("@/pages/AdminDashboard").then((m) => ({ default: m.AdminDashboard })),
);
const AdminMarketplaceLogs = lazy(() =>
  import("@/pages/AdminMarketplaceLogs").then((m) => ({
    default: m.AdminMarketplaceLogs,
  })),
);
const AdminUsersAnalytics = lazy(() =>
  import("@/pages/AdminUsersAnalytics").then((m) => ({
    default: m.AdminUsersAnalytics,
  })),
);
const AdminOrders = lazy(() =>
  import("@/pages/AdminOrders").then((m) => ({ default: m.AdminOrders })),
);
const AdminReports = lazy(() =>
  import("@/pages/AdminReports").then((m) => ({ default: m.AdminReports })),
);
const AdminServices = lazy(() =>
  import("@/pages/AdminServices").then((m) => ({ default: m.AdminServices })),
);
const AdminPricing = lazy(() =>
  import("@/pages/AdminPricing").then((m) => ({ default: m.AdminPricing })),
);
const AdminSettings = lazy(() =>
  import("@/pages/AdminSettings").then((m) => ({ default: m.AdminSettings })),
);
const AdminStock = lazy(() =>
  import("@/pages/AdminStock").then((m) => ({ default: m.AdminStock })),
);
const AdminVisoriaTest = lazy(() =>
  import("@/pages/AdminVisoriaTest").then((m) => ({
    default: m.AdminVisoriaTest,
  })),
);
const AdminSteamParser = lazy(() =>
  import("@/pages/AdminSteamParser").then((m) => ({
    default: m.AdminSteamParser,
  })),
);
const AdminSteamAccounts = lazy(() =>
  import("@/pages/AdminSteamAccounts").then((m) => ({
    default: m.AdminSteamAccounts,
  })),
);
const AdminCarlog = lazy(() =>
  import("@/pages/AdminCarlog").then((m) => ({ default: m.AdminCarlog })),
);
const AdminTron = lazy(() =>
  import("@/pages/AdminTron").then((m) => ({ default: m.AdminTron })),
);
const AdminBsv = lazy(() =>
  import("@/pages/AdminBsv").then((m) => ({ default: m.AdminBsv })),
);
const AdminTopups = lazy(() =>
  import("@/pages/AdminTopups").then((m) => ({ default: m.AdminTopups })),
);
const AdminTransactions = lazy(() =>
  import("@/pages/AdminTransactions").then((m) => ({
    default: m.AdminTransactions,
  })),
);
const AdminUsers = lazy(() =>
  import("@/pages/AdminUsers").then((m) => ({ default: m.AdminUsers })),
);
const AdminBans = lazy(() =>
  import("@/pages/AdminBans").then((m) => ({ default: m.AdminBans })),
);
// Lives OUTSIDE the admin chunk on purpose: the console is open to
// the `bybit` right (an employee without `admin`), and admin chunks
// are physically gated to admins at /assets/admin/*.
const BybitConsole = lazy(() =>
  import("@/pages/BybitConsole").then((m) => ({ default: m.BybitConsole })),
);
const Marketplaces = lazy(() =>
  import("@/pages/Marketplaces").then((m) => ({
    default: m.Marketplaces,
  })),
);
const MarketplaceProductPage = lazy(() =>
  import("@/pages/Marketplaces").then((m) => ({
    default: m.MarketplaceProductPage,
  })),
);
const MarketplaceIntegrationPage = lazy(() =>
  import("@/pages/Marketplaces").then((m) => ({
    default: m.MarketplaceIntegrationPage,
  })),
);
import { Balance } from "@/pages/Balance";
import { CategoryDetail } from "@/pages/CategoryDetail";
import { DirectTopup } from "@/pages/DirectTopup";
import { DirectTopupDetail } from "@/pages/DirectTopupDetail";
import { DirectTopupMenu } from "@/pages/DirectTopupMenu";
import { ExploreFresh } from "@/pages/ExploreFresh";
import { HomeFresh } from "@/pages/HomeFresh";
import { LayoutFresh } from "@/pages/LayoutFresh";
import { Login } from "@/pages/Login";
import { MenuDetailFresh } from "@/pages/MenuDetailFresh";
import { OrderDetail } from "@/pages/OrderDetail";
import { OrderHistory } from "@/pages/OrderHistory";
import { Playground } from "@/pages/Playground";
import { Profile } from "@/pages/Profile";
import { BYBIT_RIGHT } from "@/lib/rights";
import { RequireAdmin } from "@/pages/RequireAdmin";
import { RequireAuth } from "@/pages/RequireAuth";
import { RequireOwner } from "@/pages/RequireOwner";
import { RequireRight } from "@/pages/RequireRight";
import { SteamGifts } from "@/pages/SteamGifts";
import { SteamTopup } from "@/pages/SteamTopup";
import { Transactions } from "@/pages/Transactions";

function UnauthorizedBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    setUnauthorizedHandler(() => navigate("/login", { replace: true }));
    return () => setUnauthorizedHandler(() => undefined);
  }, [navigate]);
  return null;
}

export function App() {
  // Single-design app — Fresh shell only. The classic and modern
  // shells + the theme switcher were removed. CSS keys off
  // `data-theme="fresh"` (hardcoded on <html> in index.html).
  return (
    <BrowserRouter>
      <UnauthorizedBridge />
      <Toaster />
      <TotpPromptHost />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          element={
            <RequireAuth>
              <LayoutFresh />
            </RequireAuth>
          }
        >
          <Route path="/" element={<HomeFresh />} />
          <Route path="/explore" element={<ExploreFresh />} />
          <Route path="/menus/:id" element={<MenuDetailFresh />} />
          <Route path="/categories/:id" element={<CategoryDetail />} />
          <Route path="/orders" element={<OrderHistory />} />
          <Route path="/orders/:custom_id" element={<OrderDetail />} />
          <Route path="/transactions" element={<Transactions />} />
          <Route path="/balance" element={<Balance />} />
          <Route path="/steam-gifts" element={<SteamGifts />} />
          <Route path="/steam-topup" element={<SteamTopup />} />
          <Route path="/direct-topup" element={<DirectTopup />} />
          <Route
            path="/direct-topup/menus/:id"
            element={<DirectTopupMenu />}
          />
          <Route
            path="/direct-topup/categories/:id"
            element={<DirectTopupDetail />}
          />
          {/* Legacy / shareable shortcut: /direct-topup/:id used to be
              the detail page. Keep it working so old links don't break. */}
          <Route
            path="/direct-topup/:id"
            element={<DirectTopupDetail />}
          />
          <Route path="/profile" element={<Profile />} />
          <Route path="/api-playground" element={<Playground />} />
          {/* Single RequireAdmin layout for the whole admin section -
              the gate mounts once on entry and stays mounted while
              the operator clicks between admin pages, so we don't
              fire a fresh /me on every admin nav. RequireOwner is
              nested for the owner-only leaves.

              The Suspense + RouteChunkBoundary catch the dynamic
              import: ChunkLoadError fires when FastAPI's
              `/assets/admin/*` gate returns 403 (non-admin tampered
              with /me to render the shell), and the boundary
              redirects to / instead of leaving a blank page. */}
          <Route
            element={
              <RequireAdmin>
                <RouteChunkBoundary>
                  <Suspense
                    fallback={
                      <div className="text-sm text-slate-500 p-4">
                        Loading admin…
                      </div>
                    }
                  >
                    <Outlet />
                  </Suspense>
                </RouteChunkBoundary>
              </RequireAdmin>
            }
          >
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route
              path="/admin/transactions"
              element={<AdminTransactions />}
            />
            <Route path="/admin/settings" element={<AdminSettings />} />
            <Route path="/admin/services" element={<AdminServices />} />
            <Route path="/admin/pricing" element={<AdminPricing />} />
            <Route path="/admin/catalog" element={<AdminCatalog />} />
            <Route path="/admin/stock" element={<AdminStock />} />
            <Route
              path="/admin/visoria"
              element={<AdminVisoriaTest />}
            />
            <Route
              path="/admin/steam-parser"
              element={<AdminSteamParser />}
            />
            <Route
              path="/admin/steam-accounts"
              element={<AdminSteamAccounts />}
            />
            <Route path="/admin/carlog" element={<AdminCarlog />} />
            <Route path="/admin/codes" element={<AdminCodes />} />
            <Route
              path="/admin/marketplace-logs"
              element={<AdminMarketplaceLogs />}
            />
            <Route path="/admin/topups" element={<AdminTopups />} />
            <Route path="/admin/bans" element={<AdminBans />} />
            <Route
              path="/admin/users-analytics"
              element={<AdminUsersAnalytics />}
            />
            <Route
              element={
                <RequireOwner>
                  <Outlet />
                </RequireOwner>
              }
            >
              <Route path="/admin/reports" element={<AdminReports />} />
              <Route path="/admin/audit" element={<AdminAudit />} />
              {/* Owner's personal TRON finance tool — server gates the
                  API with require_right("owner") too. */}
              <Route path="/admin/tron" element={<AdminTron />} />
              {/* BuySellVouchers seller cabinet (real codes, money) — owner only. */}
              <Route path="/admin/bsv" element={<AdminBsv />} />
            </Route>
          </Route>
          {/* Marketplaces is a client-area section, open to every
              signed-in user (no special right). Lives outside /admin/*
              so its chunk sits in the ungated /assets/ bucket (modules
              under /pages/Marketplaces + /api/marketplaces), and every
              /web/api/marketplaces endpoint still scopes to the current
              user. URLs keep no `/admin/` prefix. */}
          <Route
            element={
              <RouteChunkBoundary>
                <Suspense
                  fallback={
                    <div className="text-sm text-slate-500 p-4">
                      Loading…
                    </div>
                  }
                >
                  <Outlet />
                </Suspense>
              </RouteChunkBoundary>
            }
          >
            <Route path="/marketplaces" element={<Marketplaces />} />
            {/* Bybit money console — `bybit` right (owner bypasses).
                Server-side every /web/api/admin/bybit endpoint checks
                the same right, this gate only keeps the SPA shell
                from rendering for others. */}
            <Route
              path="/bybit"
              element={
                <RequireRight right={BYBIT_RIGHT} fallback="/">
                  <BybitConsole />
                </RequireRight>
              }
            />
            <Route
              path="/marketplaces/product/:productId"
              element={<MarketplaceProductPage />}
            />
            <Route
              path="/marketplaces/:marketplace"
              element={<MarketplaceIntegrationPage />}
            />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
